import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectVendorsAPI, projectTagsAPI } from '../../services/api';
import { describeReminder } from './ReminderScheduleEditor';

/**
 * 供應商卡片視圖
 * 以供應商為中心，顯示每個供應商的所有問卷指派狀態（唯讀）
 */
export default function VendorCardView({ projectId }) {
  const [searchText, setSearchText] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [hideCompleted, setHideCompleted] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // 取得供應商及其指派資料
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', projectId, 'vendors-with-assignments', { tag: selectedTag, hideCompleted }],
    queryFn: () => projectVendorsAPI.getWithAssignments(projectId, {
      tag: selectedTag || undefined,
      hideCompleted: hideCompleted ? 'true' : undefined,
    }),
    staleTime: 60 * 1000,
  });

  // 取得標籤列表
  const { data: tagsData } = useQuery({
    queryKey: ['projects', projectId, 'tags'],
    queryFn: () => projectTagsAPI.getAll(projectId),
    staleTime: 5 * 60 * 1000,
  });

  const vendors = data?.vendors || [];
  const tags = tagsData?.tags || [];

  // 搜尋過濾
  const filteredVendors = vendors.filter(v =>
    v.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    v.contact_email?.toLowerCase().includes(searchText.toLowerCase())
  );

  // 分頁
  const totalPages = Math.ceil(filteredVendors.length / pageSize);
  const paginatedVendors = filteredVendors.slice((page - 1) * pageSize, page * pageSize);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">已完成</span>;
      case 'sent':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">已發送</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">待處理</span>;
    }
  };

  const getCompletionColor = (rate) => {
    if (rate >= 100) return 'text-green-600 bg-green-50';
    if (rate >= 50) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">載入失敗: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 篩選列 */}
      <div className="flex flex-wrap items-center gap-3">
        {/* 搜尋 */}
        <input
          type="text"
          placeholder="搜尋供應商..."
          value={searchText}
          onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-48"
        />

        {/* 標籤篩選 */}
        <select
          value={selectedTag}
          onChange={(e) => { setSelectedTag(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
        >
          <option value="">全部標籤</option>
          {tags.map((tag) => (
            <option key={tag.id} value={tag.name}>{tag.name}</option>
          ))}
        </select>

        {/* 隱藏已完成 */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={hideCompleted}
            onChange={(e) => { setHideCompleted(e.target.checked); setPage(1); }}
            className="rounded text-purple-600"
          />
          <span className="text-sm text-gray-700">隱藏已完成</span>
        </label>

        {/* 統計 */}
        <div className="ml-auto text-sm text-gray-500">
          共 {filteredVendors.length} 家供應商
        </div>
      </div>

      {/* 卡片列表 */}
      {filteredVendors.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-5xl mb-4">👥</div>
          <p className="text-gray-500">無符合條件的供應商</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {paginatedVendors.map((vendor) => (
            <VendorCard
              key={vendor.id}
              vendor={vendor}
              formatDate={formatDate}
              getStatusBadge={getStatusBadge}
              getCompletionColor={getCompletionColor}
            />
          ))}
        </div>
        {/* 分頁 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-gray-500">
              顯示 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filteredVendors.length)} / {filteredVendors.length} 家
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                上一頁
              </button>
              <span className="px-2 py-1 text-sm text-gray-600">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                下一頁
              </button>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}

/**
 * 單一供應商卡片（唯讀，只顯示狀態）
 */
function VendorCard({ vendor, formatDate, getStatusBadge, getCompletionColor }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasAssignments = vendor.form_assignments?.length > 0;

  // 格式化定期提醒頻率文字（支援分段格式）
  const getPeriodicReminderText = (assignment) => describeReminder(assignment);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition">
      {/* 標題區 */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{vendor.name}</h4>
            <p className="text-sm text-gray-500 truncate">{vendor.contact_email || '-'}</p>
            {vendor.email_status === 'bounced' && (
              <span
                className="inline-block mt-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs"
                title={`此信箱退信，請確認地址後重寄。${vendor.email_bounce_reason || ''}`}
              >
                ❌ Email 退信
              </span>
            )}
            {(vendor.buyer || vendor.business_unit) && (
              <div className="flex gap-3 mt-1 text-xs text-gray-500">
                {vendor.buyer && <span>採購：{vendor.buyer}</span>}
                {vendor.business_unit && <span>事業部：{vendor.business_unit}</span>}
              </div>
            )}
            {vendor.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {vendor.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          {/* 完成度 */}
          <div className={`text-center p-2 rounded-lg ${getCompletionColor(vendor.stats.completion_rate)}`}>
            <div className="text-xl font-bold">{vendor.stats.completion_rate}%</div>
            <div className="text-xs">完成度</div>
          </div>
        </div>

        {/* 統計摘要 */}
        {hasAssignments && (
          <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
            <span>已指派 {vendor.stats.total_assigned} 份問卷</span>
            <span className="text-green-600">已完成 {vendor.stats.completed}</span>
            <span className="text-orange-600">待處理 {vendor.stats.pending}</span>
          </div>
        )}
      </div>

      {/* 問卷列表 */}
      {hasAssignments && (
        <div className="border-t border-gray-200">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-between"
          >
            <span>問卷列表 ({vendor.form_assignments.length})</span>
            <span>{isExpanded ? '▲' : '▼'}</span>
          </button>

          {isExpanded && (
            <div className="px-4 pb-4 space-y-2">
              {vendor.form_assignments.map((assignment) => {
                const periodicText = getPeriodicReminderText(assignment);
                return (
                  <div
                    key={assignment.form_id}
                    className="p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 text-sm truncate">
                          {assignment.form_name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          {getStatusBadge(assignment.fill_status)}
                          {assignment.deadline && (
                            <span className={`text-xs ${new Date(assignment.deadline) < new Date() ? 'text-red-600' : 'text-gray-500'}`}>
                              截止: {formatDate(assignment.deadline)}
                            </span>
                          )}
                        </div>
                        {/* 截止日期提醒狀態 */}
                        {assignment.deadline && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {assignment.reminder_days_before > 0 && (
                              <span className="text-xs text-orange-600">
                                🔔 截止前 {assignment.reminder_days_before} 天提醒
                              </span>
                            )}
                            {assignment.remind_on_deadline && (
                              <span className="text-xs text-orange-600">
                                🔔 截止當天提醒
                              </span>
                            )}
                          </div>
                        )}
                        {/* 定期提醒狀態 */}
                        {periodicText && (
                          <div className="mt-1">
                            <span className="text-xs text-purple-600">
                              📅 定期提醒：{periodicText}
                              {assignment.next_reminder_date && (
                                <span className="text-gray-500 ml-1">
                                  （下次: {formatDate(assignment.next_reminder_date)}）
                                </span>
                              )}
                            </span>
                          </div>
                        )}
                        {assignment.fill_status === 'completed' && assignment.submitted_at && (
                          <p className="text-xs text-green-600 mt-1">
                            提交於 {formatDate(assignment.submitted_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 無指派提示 */}
      {!hasAssignments && (
        <div className="px-4 pb-4">
          <p className="text-xs text-gray-400">尚未指派問卷</p>
        </div>
      )}
    </div>
  );
}
